import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, CalendarDays, Users, BarChart3, Tag, LogOut, Zap, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import PlanBadge from '../PlanBadge';

const LANGS = [
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'ar', flag: '🇲🇦', label: 'AR' },
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, lang, setLang, isRtl } = useLanguage();

  const nav = [
    { to: '/dashboard', icon: LayoutDashboard, labelKey: 'sidebar.dashboard' },
    { to: '/properties', icon: Building2, labelKey: 'sidebar.properties' },
    { to: '/bookings', icon: CalendarDays, labelKey: 'sidebar.bookings' },
    { to: '/guests', icon: Users, labelKey: 'sidebar.guests' },
    { to: '/income', icon: BarChart3, labelKey: 'sidebar.income' },
    { to: '/pricing', icon: Tag, label: 'Pricing' },
  ];

  const handleLogout = () => { logout(); navigate('/login'); };
  const handleNavClick = () => { onClose?.(); };

  const sideStyle = {
    width: 240,
    minHeight: '100vh',
    background: 'var(--bg-card)',
    borderRight: isRtl ? 'none' : '1px solid var(--border)',
    borderLeft: isRtl ? '1px solid var(--border)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    [isRtl ? 'right' : 'left']: 0,
    top: 0,
    bottom: 0,
    zIndex: 100,
  };

  return (
    <aside style={sideStyle} className={`app-sidebar${isOpen ? ' open' : ''}`}>
      <div style={{ padding:'24px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Zap size={18} color="white" fill="white" />
          </div>
          <div>
            <div style={{ fontFamily:'Syne', fontWeight:800, fontSize:18, letterSpacing:'-0.5px' }}>Nuzlio</div>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>{t('sidebar.subtitle')}</div>
          </div>
        </div>
        <button className="mobile-close-btn" onClick={onClose} aria-label="Close menu">
          <X size={18} />
        </button>
      </div>

      <nav style={{ flex:1, padding:'16px 12px', display:'flex', flexDirection:'column', gap:4 }}>
        {nav.map(({ to, icon: Icon, labelKey, label }) => (
          <NavLink key={to} to={to} onClick={handleNavClick} style={({ isActive }) => ({ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, textDecoration:'none', fontSize:14, fontWeight:500, transition:'all 0.15s', color: isActive ? 'var(--accent-light)' : 'var(--text-secondary)', background: isActive ? 'var(--accent-glow)' : 'transparent' })}>
            <Icon size={18} />
            {label || t(labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Language switcher */}
      <div style={{ padding:'8px 12px', borderTop:'1px solid var(--border)' }}>
        <div style={{ display:'flex', gap:4 }}>
          {LANGS.map(({ code, flag, label }) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              style={{ flex:1, padding:'6px 0', background: lang === code ? 'var(--accent)' : 'var(--bg)', border: lang === code ? 'none' : '1px solid var(--border)', borderRadius:7, color: lang === code ? 'white' : 'var(--text-muted)', cursor:'pointer', fontSize:11, fontWeight:600, transition:'all 0.15s' }}
            >
              {flag} {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:'12px 12px 16px', borderTop:'1px solid var(--border)' }}>
        <div style={{ padding:'10px 12px', marginBottom:4 }}>
          <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:6, marginBottom:2 }}>
            <span style={{ fontSize:13, fontWeight:600 }}>{user?.full_name}</span>
            <PlanBadge plan={user?.subscription_plan || 'free'} />
          </div>
          <div style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</div>
        </div>
        <button onClick={handleLogout} style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 12px', borderRadius:10, background:'transparent', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:14, transition:'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color='var(--danger)'}
          onMouseLeave={e => e.currentTarget.style.color='var(--text-muted)'}>
          <LogOut size={16} /> {t('sidebar.logout')}
        </button>
      </div>
    </aside>
  );
}
